import { createClient } from '@supabase/supabase-js';
import { logBrowserDiagnostics, getChromeSpecificIssues } from './browserDetection';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const isDebugMode = import.meta.env.VITE_DEBUG_AUTH === 'true';

// Enhanced logging for debugging
const debugLog = (message: string, data?: any) => {
  if (isDebugMode && typeof console !== 'undefined') {
    console.log(`🔍 [Supabase] ${message}`, data || '');
  }
};

const errorLog = (message: string, error?: any) => {
  if (typeof console !== 'undefined') {
    console.error(`❌ [Supabase] ${message}`, error || '');
  }
};

if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars = [];
  if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
  
  errorLog(`Missing environment variables: ${missingVars.join(', ')}`);
  throw new Error(`Missing Supabase environment variables: ${missingVars.join(', ')}. Please check your .env file.`);
}

// Validate URL format
try {
  new URL(supabaseUrl);
  debugLog('Supabase URL validation passed');
} catch (error) {
  errorLog('Invalid Supabase URL format', error);
  throw new Error('Invalid Supabase URL format. Please check your VITE_SUPABASE_URL environment variable.');
}

// Enhanced connection test with retry logic
const testConnection = async (retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      debugLog(`Testing connection (attempt ${attempt}/${retries})`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        debugLog('✅ Supabase connection test successful');
        return true;
      } else {
        errorLog(`Connection test failed with status: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      errorLog(`Connection test attempt ${attempt} failed:`, error);
      
      if (attempt === retries) {
        errorLog('All connection attempts failed');
        // Don't throw here, just log the error
        return false;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  return false;
};

// Test connection on initialization (non-blocking)
testConnection().catch(error => {
  errorLog('Initial connection test failed:', error);
});

// Run browser diagnostics in debug mode
if (isDebugMode && typeof window !== 'undefined') {
  logBrowserDiagnostics().then(() => {
    getChromeSpecificIssues().then(issues => {
      if (issues.length > 0) {
        console.warn('⚠️ Chrome-specific issues detected:');
        issues.forEach(issue => console.warn(`  - ${issue}`));
      }
    });
  });
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    debug: isDebugMode,
    // Add storage key to help with Chrome's session management
    storageKey: 'spectacle-auth-token',
    // Chrome-specific: Ensure storage is available with fallback
    storage: typeof window !== 'undefined' ? {
      getItem: (key: string) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          debugLog('localStorage getItem failed, using in-memory fallback', error);
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          debugLog('localStorage setItem failed, session may not persist', error);
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          debugLog('localStorage removeItem failed', error);
        }
      }
    } : undefined
  },
  global: {
    headers: {
      'x-application-name': 'spectacle',
      'x-client-info': 'spectacle-web@1.0.0',
    },
    // Reduced timeout for faster failure detection in Chrome
    timeout: 10000 // 10 second timeout (down from 15)
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Enhanced error checking functions
export const isConnectionError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';
  
  return (
    error instanceof TypeError ||
    errorName === 'aborterror' ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('network') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('cors')
  );
};

export const getConnectionErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred.';
  
  const errorMessage = error.message?.toLowerCase() || '';
  
  if (errorMessage.includes('timeout')) {
    return 'Request timed out. Please check your internet connection and try again.';
  }
  
  if (errorMessage.includes('cors')) {
    return 'Cross-origin request blocked. Please contact support if this persists.';
  }
  
  if (isConnectionError(error)) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  return error.message || 'An unexpected error occurred. Please try again.';
};

// Health check function for monitoring
export const checkSupabaseHealth = async (): Promise<{
  isHealthy: boolean;
  latency?: number;
  error?: string;
}> => {
  const startTime = Date.now();
  
  try {
    debugLog('Performing health check');
    
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
    
    const latency = Date.now() - startTime;
    
    if (error) {
      errorLog('Health check failed:', error);
      return {
        isHealthy: false,
        latency,
        error: error.message
      };
    }
    
    debugLog(`✅ Health check passed (${latency}ms)`);
    return {
      isHealthy: true,
      latency
    };
  } catch (error) {
    const latency = Date.now() - startTime;
    errorLog('Health check exception:', error);
    
    return {
      isHealthy: false,
      latency,
      error: getConnectionErrorMessage(error)
    };
  }
};

// Export debug utilities for development
export const debugUtils = {
  testConnection,
  checkHealth: checkSupabaseHealth,
  isDebugMode,
  debugLog,
  errorLog
};